/**
 * Правка списка своих шрифтов воронки в редакторе (WEB-3943-2): вынесено из
 * `customFonts.ts`, потому что тот модуль зовёт плеер покупателя
 * (`useCustomFontFaces`) и лежит в первом экране публичной воронки, а эти
 * функции нужны только редактору (`FunnelFontsField.tsx`,
 * `store/editor/funnelSettingsActions.ts`). Поведение прежнее; пределы и
 * форма файла — в `customFonts.ts`, общая таблица случаев с сервером
 * (`customFontFilesOk`) — `__tests__/customFonts.test.tsx`.
 */
import { catalogueFontFamilies } from './fonts';
import { isCustomFontFamily } from './customFontRef';
import {
  MAX_CUSTOM_FONT_FACES,
  MAX_CUSTOM_FONT_FILES,
  MAX_CUSTOM_FONTS,
  type CustomFont,
  type CustomFontFile,
} from './customFonts';

/**
 * Потолки и уникальность файлов семейства — как у сервера
 * (`customFontFilesOk`): файлов 1..16, начертаний не больше 8, тройка
 * «жирность / начертание / поднабор» не повторяется.
 */
export function customFontFilesOk(
  files: readonly Pick<CustomFontFile, 'weight' | 'style' | 'unicodeRange'>[],
): boolean {
  // Ключ начертания — жирность и начертание слитно («400normal»): жирность —
  // число, начертание — слово, склейка не путает пары; поднабор — с «U+».
  const n = files.length;
  return (
    n > 0 &&
    n <= MAX_CUSTOM_FONT_FILES &&
    new Set(files.map((f) => f.weight + f.style)).size <=
      MAX_CUSTOM_FONT_FACES &&
    new Set(files.map((f) => f.weight + f.style + (f.unicodeRange ?? '*')))
      .size === n
  );
}

/**
 * WEB-3975: прочие семейства `@font-face` кабинета (`styles/global.css`) вне
 * каталога шрифтов воронки — тоже заняты (своё «Orbitron» перекрылось бы
 * встроенным). Сторож `themes/__tests__/customFonts.test.tsx` выводит их
 * разбором `global.css` и сверяет с общей таблицей.
 */
const CABINET_FACE_FAMILIES: readonly string[] = [
  'Barlow Condensed',
  'Cinzel',
  'Cormorant Garamond',
  'Crimson Text',
  'DM Sans',
  'Fredoka One',
  'IBM Plex Sans',
  'Inter Fallback',
  'Josefin Sans',
  'Libre Baskerville',
  'Merriweather Sans',
  'Orbitron',
  'Quicksand',
  'Rajdhani',
  'Source Sans Pro',
];

/**
 * Имена встроенных семейств (каталог `fonts.ts` с `system-ui` и запасные
 * Noto) заняты — как у сервера (`RESERVED_FAMILIES` /
 * `reservedCustomFontFamilies`): своё «Inter» перекрыло бы встроенный Inter
 * на всей странице. Оба конца сверяет общая таблица
 * `custom-fonts.cases.json`, ключ `reservedFamilies` (WEB-4071).
 */
export function reservedCustomFontFamilies(): string[] {
  return [
    ...catalogueFontFamilies(),
    'Noto Sans',
    'Noto Serif',
    ...CABINET_FACE_FAMILIES,
  ];
}

export function isReservedCustomFontFamily(family: string): boolean {
  const key = family.trim().toLowerCase();
  return reservedCustomFontFamilies().some(
    (name) => name.toLowerCase() === key,
  );
}

/**
 * Добавить загруженный файл в семейство (новое семейство заводится). Та же
 * жирность и начертание — файл заменяет ВСЕ файлы этого начертания, в том
 * числе файлы с поднаборами (WEB-3943: иначе файл автора смешался бы с
 * латиницей Google). Пределы (4 семейства, 8 начертаний, 16 файлов —
 * `customFontFilesOk`), неверное или занятое имя — список возвращается как
 * был (тот же объект).
 */
export function withCustomFontFile(
  list: CustomFont[],
  family: string,
  file: CustomFontFile,
): CustomFont[] {
  const name = family.trim();
  if (!isCustomFontFamily(name) || isReservedCustomFontFamily(name))
    return list;
  const at = list.findIndex(
    (f) => f.family.toLowerCase() === name.toLowerCase(),
  );
  if (at < 0) {
    if (list.length >= MAX_CUSTOM_FONTS) return list;
    return [...list, { family: name, files: [file] }];
  }
  const current = list[at];
  // Файл автора встаёт на место первого файла того же начертания (все
  // файлы до него — других начертаний), прочие файлы начертания уходят.
  const sameFace = (f: CustomFontFile) =>
    f.weight === file.weight && f.style === file.style;
  const first = current.files.findIndex(sameFace);
  const files = current.files.filter((f) => !sameFace(f));
  files.splice(first < 0 ? files.length : first, 0, file);
  if (!customFontFilesOk(files)) return list;
  return list.map((f, i) => (i === at ? { ...current, files } : f));
}

/** Убрать семейство целиком (имя без учёта регистра). */
export function withoutCustomFont(
  list: CustomFont[],
  family: string,
): CustomFont[] {
  const key = family.toLowerCase();
  return list.filter((f) => f.family.toLowerCase() !== key);
}
